import { formatRefDate } from '../../utils/formatRefDate.js';
import dayjs from 'dayjs';

export const initialFilter = () => ({ start: dayjs().subtract(12, 'hour').format('YYYY-MM-DD HH:mm:ss.SSS'), end: dayjs().format('YYYY-MM-DD HH:mm:ss.SSS'), interval: '5', sensors: ['All'] });

export const numeric = value => value == null || value === '' || !Number.isFinite(Number(value)) ? null : Number(value);
export const displayValue = value => numeric(value) == null ? '—' : numeric(value).toLocaleString('en-US', { maximumFractionDigits: 3 });
export function selectedReadings(rows, sensors) {
    return !sensors.length || sensors.includes('All') ? rows : rows.filter(row => sensors.includes(row.SensorCode));
}
export function hasChartReadings(rows, kind) {
    const channels = kind === 'inclination' ? ['Value1', 'Value2'] : [kind === 'volume' ? 'Value2' : 'Value1'];
    return rows.some(row => channels.some(channel => numeric(row[channel]) != null));
}
export const clockTime = value => formatRefDate(value) === '—' ? NaN : Date.parse(value.trim().replace(' ', 'T').replace(/(?:Z|[+-]\d{2}:?\d{2})$/, '') + 'Z');
export function channelInfo(code, channel) {
    if (code?.startsWith('LVL')) return channel === 'Value1' ? ['Level', 'cm'] : channel === 'Value2' ? ['Volume', 'Liter'] : ['Value3', ''];
    if (code?.startsWith('INC')) return channel === 'Value1' ? ['Trim', '°'] : channel === 'Value2' ? ['Heel', '°'] : ['Value3', ''];
    return [channel, channel === 'Value3' ? '' : code?.startsWith('TMP') ? '°C' : code?.startsWith('RPM') ? 'RPM' : ''];
}
export function valueLabel(row, channel) {
    const [label, unit] = channelInfo(row.SensorCode, channel);
    return `${label}: ${displayValue(row[channel])}${unit ? ` ${unit}` : ''}`;
}
export function enrichReadings(rows, sensors) {
    const names = new Map(sensors.map(s => [s.SensorId, s.SensorName]));
    return rows.map((row, index) => ({ ...row, SensorName: names.get(row.SensorCode) || row.SensorCode,
        key: JSON.stringify([row.AccountId, row.ObjectCode, row.SensorCode, row.RefDate, index]) }));
}
export function searchRows(rows, query) {
    const term = query.trim().toLowerCase();
    return rows.filter(row => !term || [row.SensorName, row.SensorCode, row.RefDate, formatRefDate(row.RefDate),
        ...['Value1', 'Value2', 'Value3'].map(channel => valueLabel(row, channel))].join(' ').toLowerCase().includes(term));
}
export function exportRows(rows) {
    return rows.map(row => ({ 'Date & Time': formatRefDate(row.RefDate), Sensor: row.SensorName,
        'Sensor Code': row.SensorCode, 'Value 1': valueLabel(row, 'Value1'), 'Value 2': valueLabel(row, 'Value2'), 'Value 3': valueLabel(row, 'Value3') }));
}
export const palette = ['#1682ff', '#22b573', '#8b65e9', '#ff9838', '#f65c7b', '#20afc4', '#b17a39'];
export function seriesFor(rows, kind, allCodes) {
    const codes = [...new Set(rows.map(row => row.SensorCode))];
    return codes.flatMap(code => {
        const row = rows.find(item => item.SensorCode === code);
        const channels = kind === 'inclination' ? ['Value1', 'Value2'] : [kind === 'volume' ? 'Value2' : 'Value1'];
        return channels.map((channel, n) => {
            const [label, unit] = channelInfo(code, channel);
            const name = `${row.SensorName || code}${kind === 'inclination' ? ` · ${label}` : ''}`;
            return { key: JSON.stringify([code, channel]), code, channel, name, unit,
                axis: (kind === 'engine' && code.startsWith('RPM')) || (kind === 'inclination' && channel === 'Value2') ? 'right' : 'left', color: palette[(Math.max(0, allCodes.indexOf(code)) + n) % palette.length] };
        });
    });
}
// Retain the existing first-sample selection per interval, but plot its actual RefDate.
export function chartPoints(rows, series, start, interval) {
    const origin = clockTime(start), step = Number(interval) * 60000;
    if (!Number.isFinite(origin) || !(step > 0)) return [];
    const buckets = new Map();
    for (const row of rows) {
        const time = clockTime(row.RefDate);
        if (!Number.isFinite(time)) continue;
        const bucket = Math.floor((time - origin) / step);
        if (!buckets.has(bucket)) buckets.set(bucket, { bucket });
        const point = buckets.get(bucket);
        for (const s of series.filter(s => s.code === row.SensorCode)) {
            // The old API can repeat inclusive interval-boundary rows; choose the first actual sample in each bucket.
            if (point[s.key + ':date'] && clockTime(point[s.key + ':date']) <= time) continue;
            point[s.key] = numeric(row[s.channel]); point[s.key + ':date'] = row.RefDate;
            point[s.key + ':row'] = row;
        }
    }
    const points = new Map();
    for (const bucket of buckets.values()) for (const s of series) {
        const date = bucket[s.key + ':date'];
        if (!date) continue;
        const x = clockTime(date);
        if (!points.has(x)) points.set(x, { x, bucket: bucket.bucket });
        const point = points.get(x);
        point[s.key] = bucket[s.key];
        point[s.key + ':date'] = date;
        point[s.key + ':row'] = bucket[s.key + ':row'];
    }
    return [...points.values()].sort((a, b) => a.x - b.x);
}

// Independent series prevent staggered sensor timestamps from breaking another sensor's line.
// Explicit null points still break real missing-interval gaps; never interpolate samples.
export function chartSeriesPoints(points, key, start, interval) {
    const origin = clockTime(start), step = Number(interval) * 60000;
    const data = [];
    let previous = null;
    for (const point of points) {
        if (!point[key + ':date']) continue;
        if (previous && point.bucket - previous.bucket > 1) {
            data.push({ x: origin + (previous.bucket + 1) * step, [key]: null });
        }
        data.push(point);
        previous = point;
    }
    return data;
}
